import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    school: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: '',
    color: ''
  });

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password?.length >= 8) score++;
    if (password?.length >= 12) score++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) score++;
    if (/\d/?.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/?.test(password)) score++;

    let label = '';
    let color = '';

    if (score === 0) {
      label = '';
      color = '';
    } else if (score <= 2) {
      label = 'Weak';
      color = 'text-error';
    } else if (score <= 3) {
      label = 'Fair';
      color = 'text-warning';
    } else if (score <= 4) {
      label = 'Good';
      color = 'text-primary';
    } else {
      label = 'Strong';
      color = 'text-success';
    }

    return { score, label, color };
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.school?.trim()) {
      newErrors.school = 'School affiliation is required';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { error } = await signUp(
        formData?.email,
        formData?.password,
        {
          full_name: formData?.fullName,
          school: formData?.school
        }
      );

      if (error) {
        setErrors({
          submit: error?.message || 'Registration failed. Please try again.'
        });
      } else {
        setErrors({
          submit: 'Registration successful! Please check your email to confirm your account, then sign in.'
        });
        // Reset form after successful registration
        setFormData({
          fullName: '',
          email: '',
          school: '',
          password: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      setErrors({
        submit: 'An error occurred during registration. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6 md:mb-8">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
          New Teacher?
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">
          Create your account to start building learning trails
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
        <Input
          label="Full Name"
          type="text"
          name="fullName"
          id="reg-fullName"
          autoComplete="name"
          placeholder="Enter your full name"
          value={formData?.fullName}
          onChange={handleChange}
          error={errors?.fullName}
          required
        />

        <Input
          label="Email Address"
          type="email"
          name="email"
          id="reg-email"
          autoComplete="email"
          placeholder="Enter your email"
          value={formData?.email}
          onChange={handleChange}
          error={errors?.email}
          description="We'll send a verification link to this email"
          required
        />

        <Input
          label="School Affiliation"
          type="text"
          name="school"
          id="reg-school"
          autoComplete="organization"
          placeholder="Enter your school name"
          value={formData?.school}
          onChange={handleChange}
          error={errors?.school}
          required
        />

        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            id="reg-password"
            autoComplete="new-password"
            placeholder="Create a strong password"
            value={formData?.password}
            onChange={handleChange}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-organic"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
          {formData?.password && passwordStrength?.label && (
            <div className="mt-2 flex items-center space-x-2">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    passwordStrength?.score <= 2
                      ? 'bg-error'
                      : passwordStrength?.score <= 3
                      ? 'bg-warning'
                      : passwordStrength?.score <= 4
                      ? 'bg-primary' :'bg-success'
                  }`}
                  style={{ width: `${(passwordStrength?.score / 5) * 100}%` }}
                />
              </div>
              <span className={`text-xs font-medium ${passwordStrength?.color}`}>
                {passwordStrength?.label}
              </span>
            </div>
          )}
        </div>

        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            id="reg-confirmPassword"
            autoComplete="new-password"
            placeholder="Re-enter your password"
            value={formData?.confirmPassword}
            onChange={handleChange}
            error={errors?.confirmPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-organic"
            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        {errors?.submit && (
          <div className={`p-3 md:p-4 rounded-md flex items-start ${
            errors?.submit?.includes('successful')
              ? 'bg-success/10 border border-success/20' :'bg-error/10 border border-error/20'
          }`}>
            <Icon
              name={errors?.submit?.includes('successful') ? 'CheckCircle' : 'AlertCircle'}
              size={20}
              className={`mr-3 flex-shrink-0 mt-0.5 ${
                errors?.submit?.includes('successful') ? 'text-success' : 'text-error'
              }`}
            />
            <p className={`text-sm ${
              errors?.submit?.includes('successful') ? 'text-success' : 'text-error'
            }`}>
              {errors?.submit}
            </p>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={loading}
          className="mt-6"
        >
          Create Account
        </Button>

        <div className="mt-4 text-center">
          <p className="text-xs text-muted-foreground">
            By creating an account, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;